import { useCallback, useState } from 'react';
import { ExpertRole } from '../../../../../features/experts/types/ExpertRole';
import { createExpertFromCalendlyExpert } from '../../../../../features/experts/utils/createExpertFromCalendlyExpert';
import { getRandomExpert } from '../../../../../features/experts/utils/getRandomExpert';
import { assignMedicalUserToPatient, getMedicalUserByRole } from '../../../../../utils/apis/medicaluser-service';
import { useAuthStore } from '../../../../../utils/stores/useAuthStore';
import { useExpertsStore } from '../../../../../utils/stores/useExpertsStore';
import { getUserForceAssignment, isEmailExcluded } from '../config/nutritionistAssignment';

interface UseAutoAssignExpertsReturn {
  autoAssignMissingExperts: () => Promise<void>;
  isAssigning: boolean;
  error: string | null;
  clearError: () => void;
}

/**
 * Selects the best nutritionist for a user based on assignment rules
 */
const selectNutritionist = (
  nutritionists: any[],
  userId: string
): any | null => {
  if (!nutritionists || nutritionists.length === 0) {
    console.warn('[NUTRITIONIST-SELECTION]: No nutritionists available');
    return null;
  }

  // Check for force assignments first
  const forceAssignment = getUserForceAssignment(userId);

  if (forceAssignment) {
    console.log(`[NUTRITIONIST-SELECTION]: Checking force assignment for user ${userId}`);
    
    const preferredExpert = nutritionists.find(
      nutri => nutri.email === forceAssignment.preferredEmail
    );

    if (preferredExpert) {
      console.log(`[NUTRITIONIST-SELECTION]: Found preferred expert ${preferredExpert.data.name} ${preferredExpert.data.family_name}`);
      return preferredExpert;
    } else {
      console.warn(`[NUTRITIONIST-SELECTION]: Preferred expert ${forceAssignment.preferredEmail} not found`);
      
      if (forceAssignment.fallbackToRandom) {
        console.log('[NUTRITIONIST-SELECTION]: Falling back to random selection');
        const eligibleNutritionists = nutritionists.filter(
          nutri => !isEmailExcluded(nutri.email)
        );
        return getRandomExpert(eligibleNutritionists);
      } else {
        console.error('[NUTRITIONIST-SELECTION]: No fallback allowed, no expert selected');
        return null;
      }
    }
  }

  // Normal random selection for other users
  console.log(`[NUTRITIONIST-SELECTION]: Using random selection for user ${userId}`);
  const eligibleNutritionists = nutritionists.filter(
    nutri => !isEmailExcluded(nutri.email)
  );

  if (eligibleNutritionists.length === 0) {
    console.warn('[NUTRITIONIST-SELECTION]: No eligible nutritionists found after filtering');
    return null;
  }

  return getRandomExpert(eligibleNutritionists);
};

export const useAutoAssignExperts = (): UseAutoAssignExpertsReturn => {
  const { auth } = useAuthStore();
  const { updateExperts } = useExpertsStore();
  
  const [isAssigning, setIsAssigning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = () => setError(null);

  const autoAssignMissingExperts = useCallback(async () => {
    if (!auth.userId || !auth.idToken) {
      setError('User not authenticated');
      return;
    }

    setIsAssigning(true);
    setError(null);

    try {
      // Get current experts from store
      const currentExperts = useExpertsStore.getState().experts;
      
      // Get all available experts
      const [nutritionists, physicians] = await Promise.allSettled([
        getMedicalUserByRole(auth.idToken, ExpertRole.Nutritionist),
        getMedicalUserByRole(auth.idToken, ExpertRole.Physician)
      ]);

      const assignments = [];

      // Check and assign nutritionist if missing
      if (!currentExperts.nutritionist && nutritionists.status === 'fulfilled' && nutritionists.value) {
        try {
          const selectedNutritionist = selectNutritionist(nutritionists.value, auth.userId);

          if (selectedNutritionist) {
            const newExpert = createExpertFromCalendlyExpert(selectedNutritionist);
            const assignedNutritionist = await assignMedicalUserToPatient(
              newExpert.id!,
              auth.userId,
              auth.idToken
            );
            
            if (assignedNutritionist) {
              console.log(`[AUTO-ASSIGN]: Nutritionist ${selectedNutritionist.data.name} ${selectedNutritionist.data.family_name} assigned to user=${auth.userId}`);
              assignments.push({ type: 'nutritionist', expert: assignedNutritionist });
            }
          } else {
            console.warn('[AUTO-ASSIGN]: No nutritionist could be selected for assignment');
          }
        } catch (error) {
          console.warn('Failed to auto-assign nutritionist:', error);
        }
      }

      // Check and assign physician if missing
      if (!currentExperts.physician && physicians.status === 'fulfilled' && physicians.value) {
        try {
          // Use the same logic as useAssignPhysician
          const selectedPhysician = getRandomExpert(physicians.value);
          const newExpert = createExpertFromCalendlyExpert(selectedPhysician);
          const assignedPhysician = await assignMedicalUserToPatient(
            newExpert.id!,
            auth.userId,
            auth.idToken
          );
          
          if (assignedPhysician) {
            console.log(`[AUTO-ASSIGN]: Physician ${selectedPhysician.data.name} ${selectedPhysician.data.family_name} assigned to user=${auth.userId}`);
            assignments.push({ type: 'physician', expert: assignedPhysician });
          }
        } catch (error) {
          console.warn('Failed to auto-assign physician:', error);
        }
      }

      // Update store with new assignments
      if (assignments.length > 0) {
        const updates: any = {};
        assignments.forEach(({ type, expert }) => {
          if (type === 'nutritionist') updates.nutritionist = expert;
          if (type === 'physician') updates.physician = expert;
        });
        
        updateExperts(updates);
        console.log(`[AUTO-ASSIGN]: Successfully assigned ${assignments.length} expert(s)`);
      } else {
        console.log('[AUTO-ASSIGN]: No experts needed assignment');
      }

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to auto-assign experts';
      setError(errorMessage);
      console.error('Error in auto-assign experts:', err);
    } finally {
      setIsAssigning(false);
    }
  }, [auth.userId, auth.idToken, updateExperts]);

  return {
    autoAssignMissingExperts,
    isAssigning,
    error,
    clearError,
  };
}; 